import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

import { get, put } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import InfoCard from "mobile/components/InfoCard";
import { IosCard, IosPage } from "mobile/components/IosParity";

const SAT_PAYMENT_FORM_OPTIONS = [
  { value: "01", label: "01 - Cash" },
  { value: "02", label: "02 - Check" },
  { value: "03", label: "03 - Electronic Transfer" },
  { value: "04", label: "04 - Credit Card" },
  { value: "28", label: "28 - Debit Card" },
  { value: "99", label: "99 - To Be Defined" },
];

const getPaymentMethodLabel = (satPaymentForm, paymentMethod) => {
  if (paymentMethod === "credit_memo") return "Credit Memo";
  const match = SAT_PAYMENT_FORM_OPTIONS.find((option) => option.value === String(satPaymentForm || "").trim().toUpperCase());
  return match?.label || satPaymentForm || "—";
};

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const formatDate = (v) => (v ? String(v).slice(0, 10) : "—");

const formatMoney = (value) => {
  const n = value == null ? 0 : Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

export default function PaymentDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState({
    amount: "",
    currency: "USD",
    exchangeRate: "",
    satPaymentForm: "03",
    paymentDate: "",
    referenceNumber: "",
    notes: "",
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-payment", documentId],
    queryFn: () =>
      get(`/api/payments/${documentId}`, {
        populate: { invoice: true, purchaseOrder: true },
      }),
    enabled: !!documentId,
    staleTime: 10_000,
  });

  const payment = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);
  const invoice = useMemo(() => {
    const raw = payment?.invoice?.data || payment?.invoice || null;
    return raw ? normalize(raw) : null;
  }, [payment]);
  const po = useMemo(() => {
    const raw = payment?.purchaseOrder?.data || payment?.purchaseOrder || null;
    return raw ? normalize(raw) : null;
  }, [payment]);

  useEffect(() => {
    if (!payment) return;
    setDraft({
      amount: payment.amount != null ? String(payment.amount) : "",
      currency: payment.currency || "USD",
      exchangeRate: payment.exchangeRate != null ? String(payment.exchangeRate) : "",
      satPaymentForm: payment.satPaymentForm || "03",
      paymentDate: payment.paymentDate ? String(payment.paymentDate).slice(0, 10) : "",
      referenceNumber: payment.referenceNumber || "",
      notes: payment.notes || "",
    });
  }, [payment, editOpen]);

  const saveMutation = useMutation({
    mutationFn: () =>
      put(`/api/payments/${payment?.documentId || documentId}`, {
        data: {
          amount: draft.amount === "" ? null : Number(draft.amount),
          currency: draft.currency,
          exchangeRate: draft.exchangeRate === "" ? null : Number(draft.exchangeRate),
          satPaymentForm: payment?.paymentMethod === "credit_memo" ? null : draft.satPaymentForm,
          paymentDate: draft.paymentDate || null,
          referenceNumber: draft.referenceNumber || null,
          notes: draft.notes || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-payment", documentId] });
      setEditOpen(false);
    },
  });

  if (isLoading) return <LinearProgress />;
  if (error || !payment) {
    return (
      <IosPage>
        <Typography variant="subtitle1" fontWeight={900}>
          Payment not found
        </Typography>
      </IosPage>
    );
  }

  const amountUSD = payment.amountInUSD != null ? payment.amountInUSD : payment.amount;

  return (
    <IosPage>
      <MobileTopBar
        title="Payment"
        subtitle={payment.paymentNumber || ""}
        onBack={() => navigate("/m/payments")}
        right={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip size="small" variant="outlined" label={formatMoney(amountUSD)} />
            <IconButton aria-label="Edit payment" size="small" onClick={() => setEditOpen(true)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Stack>
        }
      />

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <InfoCard
          title="Summary"
          rows={[
            { label: "Payment #", value: payment.paymentNumber || "—" },
            { label: "Method", value: getPaymentMethodLabel(payment.satPaymentForm, payment.paymentMethod) },
            { label: "Date", value: formatDate(payment.paymentDate) },
            { label: "Currency", value: payment.currency || "USD" },
            { label: "Amount", value: formatMoney(payment.amount) },
            { label: "Amount (USD)", value: payment.amountInUSD != null ? formatMoney(payment.amountInUSD) : "—" },
          ]}
        />

        <InfoCard
          title="Linked"
          rows={[
            { label: "Invoice", value: invoice?.salesOrderNumber || "—" },
            { label: "Purchase order", value: po?.purchaseOrderNumber || "—" },
            { label: "Reference", value: payment.referenceNumber || "—" },
          ]}
        />

        {payment.notes ? (
          <IosCard>
              <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {payment.notes}
              </Typography>
          </IosCard>
        ) : null}
      </Stack>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullScreen>
        <IosPage>
          <MobileTopBar title="Edit Payment" subtitle={payment.paymentNumber || ""} onBack={() => setEditOpen(false)} />
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <TextField label="Amount" type="number" value={draft.amount} onChange={(e) => setDraft((p) => ({ ...p, amount: e.target.value }))} />
            <FormControl fullWidth size="small">
              <InputLabel>Currency</InputLabel>
              <Select label="Currency" value={draft.currency} onChange={(e) => setDraft((p) => ({ ...p, currency: e.target.value }))}>
                {["USD", "MXN"].map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Exchange rate"
              type="number"
              value={draft.exchangeRate}
              onChange={(e) => setDraft((p) => ({ ...p, exchangeRate: e.target.value }))}
              helperText="Only needed if currency is MXN."
            />
            {payment.paymentMethod !== "credit_memo" ? (
              <FormControl fullWidth size="small">
                <InputLabel>Method (SAT)</InputLabel>
                <Select
                  label="Method (SAT)"
                  value={draft.satPaymentForm}
                  onChange={(e) => setDraft((p) => ({ ...p, satPaymentForm: e.target.value }))}
                >
                  {SAT_PAYMENT_FORM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <TextField
              label="Payment date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={draft.paymentDate}
              onChange={(e) => setDraft((p) => ({ ...p, paymentDate: e.target.value }))}
            />
            <TextField
              label="Reference"
              value={draft.referenceNumber}
              onChange={(e) => setDraft((p) => ({ ...p, referenceNumber: e.target.value }))}
            />
            <TextField label="Notes" multiline minRows={4} value={draft.notes} onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))} />
            <Divider />
            <Stack direction="row" spacing={0.75}>
              <Button variant="outlined" fullWidth onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" fullWidth disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                Save
              </Button>
            </Stack>
            {saveMutation.isPending ? <LinearProgress /> : null}
          </Stack>
        </IosPage>
      </Dialog>
    </IosPage>
  );
}
